import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowsToDot,
    faChevronLeft,
    faCircleInfo,
} from '@fortawesome/free-solid-svg-icons';
import { Button, ConfirmModal, SearchFilterInput } from '@quyva/shared-ui';
import { getRepoIcon } from '@quyva/common';
import { RepoProductMappings } from './repoProductMapping';

const RepoListPane = ({
    repos,
    repoHasFileProductMappings,
    selectedRepo,
    onSelectRepo,
    className,
}) => {
    const [search, setSearch] = useState('');

    const filteredRepos = (repos || [])
        .filter((repo) =>
            (repo.name || '').toLowerCase().includes(search.trim().toLowerCase())
        )
        .sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className={className}>
            <div className='px-2 py-2 border-b border-backgrounds-border'>
                <SearchFilterInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder='Search repositories'
                    className='w-full'
                />
            </div>

            <div className='flex-1 overflow-y-auto p-1.5'>
                {!repos || repos.length === 0 ? (
                    <div className='w-full h-full flex flex-col items-center justify-center text-center text-texts-secondary300 p-3'>
                        <FontAwesomeIcon
                            icon={faArrowsToDot}
                            size='2x'
                            className='mb-2'
                        />
                        <div className='font-semibold text-sm'>
                            No repositories available for product mapping
                        </div>
                    </div>
                ) : filteredRepos.length === 0 ? (
                    <div className='w-full h-full flex items-center justify-center text-center text-texts-secondary300 font-semibold text-sm p-3'>
                        No repositories match your search
                    </div>
                ) : (
                    <div className='flex flex-col gap-1'>
                        {filteredRepos.map((repo) => {
                            const isMapped =
                                !!repoHasFileProductMappings?.[repo.repoid];
                            const isSelected =
                                selectedRepo?.repoid === repo.repoid;
                            return (
                                <button
                                    key={repo.repoid}
                                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-xl border text-left transition-colors ${
                                        isSelected
                                            ? 'bg-backgrounds-primary100 border-backgrounds-primary300 text-texts-tertiary shadow-inner'
                                            : 'bg-backgrounds-white border-transparent text-texts-secondary300 hover:bg-backgrounds-primary100 hover:border-backgrounds-primary200'
                                    }`}
                                    onClick={() => onSelectRepo(repo)}
                                >
                                    <div className='flex items-center truncate min-w-0'>
                                        <FontAwesomeIcon
                                            icon={getRepoIcon(repo)}
                                            className={`mr-2 shrink-0 drop-shadow-sm ${
                                                isSelected
                                                    ? 'text-backgrounds-primary300'
                                                    : 'text-icons-primary'
                                            }`}
                                        />
                                        <span
                                            className='truncate text-sm'
                                            title={repo.name}
                                        >
                                            {repo.name}
                                        </span>
                                    </div>
                                    {isMapped && (
                                        <FontAwesomeIcon
                                            icon={faArrowsToDot}
                                            className='ml-2 text-icons-primary shrink-0 drop-shadow-sm'
                                            title='Has product mappings'
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

const EmptyDetailPane = () => (
    <div className='w-full h-full flex flex-col items-center justify-center text-center text-texts-secondary300 p-6'>
        <FontAwesomeIcon icon={faArrowsToDot} size='2x' className='mb-3' />
        <div className='font-semibold'>
            Select a repository from the list to manage its product mappings
        </div>
    </div>
);

export const ProductMappingWorkspace = ({
    user,
    repos,
    sharedRepos,
    allReposFilesData,
    productRepoMappings,
    repoHasFileProductMappings,
    getRepoFilesData,
    getRepoProductMappings,
    isSmallScreen,
    onDirtyChange,
}) => {
    const [selectedRepo, setSelectedRepo] = useState(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [confirmSwitchOpen, setConfirmSwitchOpen] = useState(false);
    const [pendingRepo, setPendingRepo] = useState(null);

    const handleDirtyChange = (dirty) => {
        setHasUnsavedChanges(!!dirty);
        onDirtyChange?.(!!dirty);
    };

    const requestSelectRepo = (repoOrNull) => {
        const isSameRepo =
            selectedRepo &&
            repoOrNull &&
            selectedRepo.repoid === repoOrNull.repoid;

        if (hasUnsavedChanges && !isSameRepo) {
            setPendingRepo(repoOrNull);
            setConfirmSwitchOpen(true);
            return;
        }

        setSelectedRepo(repoOrNull);
    };

    const confirmSwitch = () => {
        setConfirmSwitchOpen(false);
        setHasUnsavedChanges(false);
        onDirtyChange?.(false);
        setSelectedRepo(pendingRepo);
        setPendingRepo(null);
    };

    const cancelSwitch = () => {
        setConfirmSwitchOpen(false);
        setPendingRepo(null);
    };

    const detailPane = selectedRepo ? (
        <RepoProductMappings
            user={user}
            selectedRepo={selectedRepo}
            sharedRepos={sharedRepos}
            allReposFilesData={allReposFilesData}
            productRepoMappings={productRepoMappings}
            getRepoFilesData={getRepoFilesData}
            getRepoProductMappings={getRepoProductMappings}
            isSmallScreen={isSmallScreen}
            onDirtyChange={handleDirtyChange}
        />
    ) : (
        <EmptyDetailPane />
    );

    return (
        <>
            <div
                className={
                    isSmallScreen
                        ? 'w-[92vw] h-[82vh] flex flex-col'
                        : 'w-[80vw] h-[79vh] flex flex-col'
                }
            >
                <div className='flex items-start gap-2 px-3 py-2 border-b border-backgrounds-border bg-backgrounds-white rounded-t-xl text-xs text-texts-secondary300'>
                    <FontAwesomeIcon
                        icon={faCircleInfo}
                        className='text-texts-secondary400 mt-0.5'
                    />
                    <span>
                        Pick a repository from the list to view and edit its
                        product mappings. Each file in the selected repository
                        can be assigned a product category and one or more
                        products; files left unmapped show up as "Not mapped".
                    </span>
                </div>

                <div className='flex-1 min-h-0'>
                    {isSmallScreen ? (
                        selectedRepo ? (
                            <div className='w-full h-full flex flex-col'>
                                <div className='px-2 py-2 border-b border-backgrounds-border bg-backgrounds-primary100'>
                                    <Button
                                        text='Back to repository list'
                                        icon={faChevronLeft}
                                        type='success'
                                        onClick={() => requestSelectRepo(null)}
                                        hoverText='Back to repository list'
                                    />
                                </div>
                                <div className='flex-1 min-h-0'>
                                    {detailPane}
                                </div>
                            </div>
                        ) : (
                            <RepoListPane
                                repos={repos}
                                repoHasFileProductMappings={
                                    repoHasFileProductMappings
                                }
                                selectedRepo={selectedRepo}
                                onSelectRepo={requestSelectRepo}
                                className='w-full h-full flex flex-col'
                            />
                        )
                    ) : (
                        <div className='w-full h-full flex'>
                            <RepoListPane
                                repos={repos}
                                repoHasFileProductMappings={
                                    repoHasFileProductMappings
                                }
                                selectedRepo={selectedRepo}
                                onSelectRepo={requestSelectRepo}
                                className='w-[260px] min-w-[220px] h-full flex flex-col border-r border-backgrounds-border bg-backgrounds-white rounded-l-xl'
                            />
                            <div className='flex-1 h-full min-w-0 overflow-auto'>
                                {detailPane}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {confirmSwitchOpen && (
                <ConfirmModal
                    modalText={
                        pendingRepo
                            ? `You have unsaved changes. Are you sure you want to discard them and switch to '${pendingRepo.name}'?`
                            : "You have unsaved changes. Are you sure you want to discard them and go back to the repository list?"
                    }
                    onConfirm={confirmSwitch}
                    onCancel={cancelSwitch}
                    confirmText='Discard Changes'
                    cancelText='Stay'
                />
            )}
        </>
    );
};
